import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { DataScopePanel, type ScopeSectionMeta } from './DataScopePanel';
import { createSpecialty } from '../../lib/prompts-api';
import { cn } from '../../lib/utils';

interface Props {
  open: boolean;
  sections: ScopeSectionMeta[];
  onClose: () => void;
  onCreated: (slug: string) => void;
}

function deriveSlug(input: string): string {
  return input.toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 64);
}

export function NewSpecialtyModal({ open, sections, onClose, onCreated }: Props) {
  const [displayName, setDisplayName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [group, setGroup] = useState<'validated' | 'pediatric' | 'adult'>('adult');
  const [scope, setScope] = useState<string[]>([]);
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setDisplayName('');
      setSlug('');
      setSlugManuallyEdited(false);
      setGroup('adult');
      setScope([]);
      setBody('');
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  useEffect(() => {
    if (!slugManuallyEdited) {
      setSlug(deriveSlug(displayName));
    }
  }, [displayName, slugManuallyEdited]);

  const handleToggle = (id: string) => {
    setScope(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSubmit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const { specialty } = await createSpecialty({ displayName, slug, group, dataScope: scope, body });
      onCreated(specialty.slug);
    } catch (err) {
      // Server errors come back from jsonFetch as `${status}: ${text}`. Strip the status prefix.
      const raw = err instanceof Error ? err.message : 'Create failed';
      const stripped = raw.replace(/^\d+:\s*/, '');
      // If the stripped message looks like JSON, try to pull the error field.
      let display = stripped;
      try {
        const parsed = JSON.parse(stripped);
        if (parsed && typeof parsed.error === 'string') display = parsed.error;
      } catch { /* not JSON, use stripped as-is */ }
      setError(display);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>+ New specialty</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label htmlFor="display-name" className="block text-xs font-bold text-gray-900 mb-1">Display name</label>
            <Input id="display-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          </div>
          <div>
            <label htmlFor="slug" className="block text-xs font-bold text-gray-900 mb-1">Slug</label>
            <Input
              id="slug"
              value={slug}
              onChange={(e) => { setSlug(e.target.value); setSlugManuallyEdited(true); }}
            />
            <p className="text-[10px] text-gray-600 mt-1">Auto-derived from display name; editable.</p>
          </div>
          <div>
            <span className="block text-xs font-bold text-gray-900 mb-1">Group</span>
            <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
              {(['validated', 'pediatric', 'adult'] as const).map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => setGroup(g)}
                  className={cn(
                    'px-3 py-1 text-xs',
                    group === g ? 'bg-blue-50 text-blue-700 font-semibold' : 'bg-white text-gray-700',
                  )}
                >
                  {g[0].toUpperCase() + g.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <div>
            <span className="block text-xs font-bold text-gray-900 mb-1">
              Data scope <span className="font-normal text-gray-600">— click to opt in</span>
            </span>
            <div className="border border-gray-200 rounded-md overflow-hidden">
              <DataScopePanel
                sections={sections}
                selected={scope}
                baseline={[]}
                mode="edit"
                onToggle={handleToggle}
              />
            </div>
            {scope.length === 0 && (
              <p className="text-[10px] text-gray-600 mt-1">
                Selected 0 of {sections.length} — no patient data will be sent to the AI until you opt in.
              </p>
            )}
          </div>
          <div>
            <label htmlFor="body" className="block text-xs font-bold text-gray-900 mb-1">Prompt body</label>
            <Textarea
              id="body"
              aria-label="Prompt body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={8}
              className="font-mono text-xs"
              placeholder="Paste the system prompt for this specialty here."
            />
          </div>

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700">{error}</div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} className="bg-blue-600 hover:bg-blue-700 text-white">
            {submitting ? 'Creating…' : '+ Create specialty'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
